import { useEffect, useState } from 'react';

import { client } from '@/api/common';

import type { LowRatedReview } from './use-dashboard-performance';

export const LOW_RATED_REVIEW_INITIAL_VISIBLE = 4;
export const LOW_RATED_REVIEW_PAGE_SIZE = 10;

export type LowRatedReviewsPage = {
  data: LowRatedReview[];
  meta: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
  };
};

export type CarRatingReviewsScope = 'low-rated' | 'all';

function mergeLowRatedReviews(
  current: LowRatedReview[],
  incoming: LowRatedReview[]
) {
  if (incoming.length === 0) {
    return current;
  }

  const seen = new Set(current.map((review) => review.reservationId));
  const merged = [...current];

  for (const review of incoming) {
    if (seen.has(review.reservationId)) {
      continue;
    }

    seen.add(review.reservationId);
    merged.push(review);
  }

  return merged;
}

type FetchCarReviewsPageOptions = {
  carId: number;
  from: string;
  to: string;
  page: number;
  scope: CarRatingReviewsScope;
  perPage?: number;
};

async function fetchCarReviewsPage({
  carId,
  from,
  to,
  page,
  scope,
  perPage = LOW_RATED_REVIEW_PAGE_SIZE,
}: FetchCarReviewsPageOptions): Promise<LowRatedReviewsPage> {
  const response = await client.get<LowRatedReviewsPage>(
    `v2/internal/partners/dashboard/car-ratings/${carId}/reviews`,
    {
      params: {
        from,
        to,
        page,
        per_page: perPage,
        ...(scope === 'low-rated' ? { low_rated_only: true } : {}),
      },
    }
  );

  return response.data;
}

type UsePaginatedCarReviewsOptions = {
  car: { id: number; reviewCount: number };
  from: string;
  to: string;
  expanded: boolean;
  scope?: CarRatingReviewsScope;
};

export function usePaginatedCarReviews({
  car,
  from,
  to,
  expanded,
  scope = 'low-rated',
}: UsePaginatedCarReviewsOptions) {
  const [reviews, setReviews] = useState<LowRatedReview[]>([]);
  const [totalCount, setTotalCount] = useState(car.reviewCount);
  const [visibleCount, setVisibleCount] = useState(0);
  const [loadedPage, setLoadedPage] = useState(0);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  useEffect(() => {
    setReviews([]);
    setTotalCount(car.reviewCount);
    setVisibleCount(0);
    setLoadedPage(0);
    setTotalPages(null);
    setIsLoadingMore(false);
    setIsLoadingReviews(false);
  }, [car.id, car.reviewCount, from, to]);

  useEffect(() => {
    if (!expanded) {
      setVisibleCount(0);
      return;
    }

    setVisibleCount((current) => {
      if (current > 0) {
        return current;
      }

      return Math.min(LOW_RATED_REVIEW_INITIAL_VISIBLE, reviews.length);
    });
  }, [expanded, reviews.length]);

  useEffect(() => {
    if (!expanded || loadedPage > 0) {
      return;
    }

    let cancelled = false;

    const loadFirstPage = async () => {
      setIsLoadingReviews(true);

      try {
        const result = await fetchCarReviewsPage({
          carId: car.id,
          from,
          to,
          page: 1,
          scope,
        });

        if (cancelled) {
          return;
        }

        setTotalCount(result.meta.totalCount);
        setReviews(result.data);
        setVisibleCount((current) =>
          Math.max(
            current,
            Math.min(LOW_RATED_REVIEW_INITIAL_VISIBLE, result.data.length)
          )
        );
        if (result.data.length > 0) {
          setLoadedPage(1);
          setTotalPages(result.meta.totalPages);
        }
      } catch {
        // Pagination retries from page 1 on show more.
      } finally {
        if (!cancelled) {
          setIsLoadingReviews(false);
        }
      }
    };

    void loadFirstPage();

    return () => {
      cancelled = true;
    };
  }, [car.id, car.reviewCount, expanded, from, loadedPage, scope, to]);

  const showMoreCount = Math.min(
    LOW_RATED_REVIEW_PAGE_SIZE,
    totalCount - visibleCount
  );
  const canShowMore =
    visibleCount > 0 && visibleCount < totalCount && !isLoadingReviews;
  const visibleReviews = reviews.slice(0, visibleCount);

  const showMore = async () => {
    if (!canShowMore || isLoadingMore) {
      return;
    }

    const target = Math.min(visibleCount + showMoreCount, totalCount);

    setIsLoadingMore(true);

    try {
      let nextReviews = reviews;
      let nextTotalCount = totalCount;

      if (nextReviews.length < target) {
        const hasMorePages = totalPages === null || loadedPage < totalPages;

        if (hasMorePages) {
          const nextPage = loadedPage === 0 ? 1 : loadedPage + 1;
          const result = await fetchCarReviewsPage({
            carId: car.id,
            from,
            to,
            page: nextPage,
            scope,
          });

          nextReviews =
            nextPage === 1
              ? result.data
              : mergeLowRatedReviews(nextReviews, result.data);
          nextTotalCount = result.meta.totalCount;
          setLoadedPage(nextPage);
          setTotalPages(result.meta.totalPages);
          setTotalCount(nextTotalCount);
        }
      }

      setReviews(nextReviews);
      setVisibleCount(Math.min(target, nextReviews.length, nextTotalCount));
    } catch {
      // Avoid crashing the dashboard when the reviews endpoint is unavailable.
    } finally {
      setIsLoadingMore(false);
    }
  };

  return {
    visibleReviews,
    totalCount,
    showMoreCount,
    canShowMore,
    isLoadingMore,
    isLoadingReviews,
    showMore,
  };
}
